import { Box, Radio, RadioGroup, Sheet, Switch, Typography } from '@mui/joy';
import { CaretDown } from '@phosphor-icons/react';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { POST_ACCESS, POST_ACCESS_OPTIONS } from '../../../config/configPostAccess';

/**
 * Раскрывающийся блок дополнительных настроек записи.
 *
 * Свёрнут по умолчанию: у подавляющего большинства записей лайки и комментарии
 * разрешены, и постоянно показывать два переключателя — шум в основном сценарии.
 *
 * ВАЖНО про названия на сервере: поля называются `noLikes` / `noComments`, но
 * значат ОБРАТНОЕ тому, что читается — `1` разрешает, `0` запрещает. Здесь
 * работаем в понятных терминах «разрешено», а конвертация — на месте отправки.
 */
function PostPermissions({
    allowLikes = true,
    allowComments = true,
    access = POST_ACCESS.PUBLIC,
    onChangeLikes,
    onChangeComments,
    onChangeAccess,
}) {
    const [open, setOpen] = useState(false);

    const accessOption = POST_ACCESS_OPTIONS.find(o => o.value === Number(access));

    // Свёрнутый блок не должен выглядеть «пустым»: если что-то уже запрещено или
    // сужен доступ, подписываем это прямо в шапке, иначе настройка потеряется
    // из виду.
    const restrictions = [
        Number(access) !== POST_ACCESS.PUBLIC && accessOption?.label.toLowerCase(),
        !allowLikes && 'лайки выключены',
        !allowComments && 'комментарии выключены',
    ].filter(Boolean).join(', ');

    return (
        <Sheet variant="soft" sx={{ borderRadius: '26px', px: 2, overflow: 'hidden' }}>
            <Box
                role="button"
                tabIndex={0}
                onClick={() => setOpen(v => !v)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen(v => !v);
                    }
                }}
                aria-expanded={open}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 1.75,
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
            >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography level="title-md">Настройки</Typography>
                    {!open && restrictions.length > 0 && (
                        <Typography level="body-sm" textColor="text.tertiary">
                            {restrictions}
                        </Typography>
                    )}
                </Box>

                <Box
                    component={motion.div}
                    animate={{ rotate: open ? 180 : 0 }}
                    transition={{ duration: 0.22 }}
                    sx={{ display: 'flex', color: 'text.tertiary' }}
                >
                    <CaretDown size={18} />
                </Box>
            </Box>

            <AnimatePresence initial={false}>
                {open && (
                    <Box
                        component={motion.div}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        sx={{ overflow: 'hidden' }}
                    >
                        <Box sx={{ height: '1px', backgroundColor: 'divider', mb: 3 }} />

                        <Typography level="body-xs" sx={{ textTransform: 'uppercase', mb: 2 }}>
                            Уровень доступа
                        </Typography>
                        <RadioGroup
                            value={Number(access)}
                            onChange={(e) => onChangeAccess?.(Number(e.target.value))}
                        >
                            {POST_ACCESS_OPTIONS.map(option => (
                                <Radio
                                    key={option.value}
                                    value={option.value}
                                    label={
                                        <Typography level="body-md">{option.label}</Typography>
                                    }
                                />
                            ))}
                        </RadioGroup>

                        <Box sx={{ height: '1px', backgroundColor: 'divider', my: 2 }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                            <Typography level="body-md">Разрешить лайкать</Typography>
                            <Switch
                                checked={allowLikes}
                                onChange={(e) => onChangeLikes?.(e.target.checked)}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, pt: 1, pb: 2 }}>
                            <Typography level="body-md">Разрешить комментировать</Typography>
                            <Switch
                                checked={allowComments}
                                onChange={(e) => onChangeComments?.(e.target.checked)}
                            />
                        </Box>
                    </Box>
                )}
            </AnimatePresence>
        </Sheet>
    );
}

export default PostPermissions;
